/*
 * BCM-891 — the version history Excel, styled like the popup: the version
 * header row and the field column on a grey background, the values on white,
 * every cell bordered, headers bold, values centred and wrapped.
 *
 * The community build of SheetJS in the host app writes no cell styles, and the
 * styling fork (xlsx-js-style) is not in the BMS host build. So the workbook is
 * written with plain SheetJS as before, then its stylesheet is replaced and each
 * cell pointed at a style, through the zip container SheetJS already ships
 * (XLSX.CFB). No new dependency.
 */

const HEADER_FILL = "FFF2F2F2";
const BORDER_COLOR = "FFBFBFBF";

// cellXfs indexes: 0 default, 1 version header row, 2 field column, 3 values.
const STYLE_HEADER = 1;
const STYLE_FIELD = 2;
const STYLE_VALUE = 3;

const side = (name) => `<${name} style="thin"><color rgb="${BORDER_COLOR}"/></${name}>`;

const STYLES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<fonts count="2">' +
  '<font><sz val="11"/><name val="Calibri"/><family val="2"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font>' +
  "</fonts>" +
  '<fills count="3">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  `<fill><patternFill patternType="solid"><fgColor rgb="${HEADER_FILL}"/><bgColor indexed="64"/></patternFill></fill>` +
  "</fills>" +
  '<borders count="2">' +
  "<border><left/><right/><top/><bottom/><diagonal/></border>" +
  `<border>${side("left")}${side("right")}${side("top")}${side("bottom")}<diagonal/></border>` +
  "</borders>" +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="4">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="49" fontId="1" fillId="2" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1">' +
  '<alignment horizontal="center" vertical="center" wrapText="1"/></xf>' +
  '<xf numFmtId="49" fontId="1" fillId="2" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1">' +
  '<alignment horizontal="left" vertical="center" wrapText="1"/></xf>' +
  '<xf numFmtId="49" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1">' +
  '<alignment horizontal="center" vertical="center" wrapText="1"/></xf>' +
  "</cellXfs>" +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  "</styleSheet>";

// Which style a cell takes, by its position: row 1 is the version header, column A the field names.
const styleFor = (colLetters, rowNumber) => {
  if (rowNumber === 1) return STYLE_HEADER;
  if (colLetters === "A") return STYLE_FIELD;
  return STYLE_VALUE;
};

// Point every cell of the sheet XML at its style, replacing any s="" SheetJS wrote.
export const applyCellStyles = (sheetXml) =>
  sheetXml.replace(/<c r="([A-Z]+)(\d+)"([^>]*?)(\/?)>/g, (m, col, row, attrs, selfClose) => {
    const rest = attrs.replace(/\s+s="\d+"/, "");
    return `<c r="${col}${row}" s="${styleFor(col, Number(row))}"${rest}${selfClose}>`;
  });

const encode = (s) => new TextEncoder().encode(s);
const decode = (u8) => new TextDecoder("utf-8").decode(u8);

/*
 * rows:   array of arrays, row 0 the version header, column 0 the field names
 * merges: SheetJS merge ranges
 * cols:   SheetJS column widths
 * Returns the finished .xlsx as a Uint8Array.
 */
export const buildStyledWorkbook = (XLSX, { rows, merges = [], cols, sheetName }) => {
  // Every cell as text, so values like "0002" and "13546131N" keep exactly what the popup shows.
  const sheet = XLSX.utils.aoa_to_sheet(rows.map((r) => r.map((v) => (v == null ? "" : String(v)))));
  sheet["!merges"] = merges;
  if (cols) sheet["!cols"] = cols;
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sheetName);

  const written = new Uint8Array(XLSX.write(book, { type: "array", bookType: "xlsx" }));
  const zip = XLSX.CFB.read(written, { type: "array" });
  // Entries are held under the container root ("Root Entry/xl/styles.xml"), so they are found by suffix.
  const entry = (path) => {
    const i = zip.FullPaths.findIndex((p) => p.endsWith(`/${path}`));
    return i === -1 ? null : zip.FileIndex[i];
  };
  const stylesEntry = entry("xl/styles.xml");
  const sheetEntry = entry("xl/worksheets/sheet1.xml");
  if (!stylesEntry || !sheetEntry) {
    // Not the layout expected: hand back the plain workbook rather than a broken one.
    return written;
  }
  stylesEntry.content = encode(STYLES_XML);
  sheetEntry.content = encode(applyCellStyles(decode(sheetEntry.content)));
  return new Uint8Array(XLSX.CFB.write(zip, { type: "array", fileType: "zip" }));
};

// Save the workbook in the browser, as XLSX.writeFile did.
export const downloadStyledWorkbook = (XLSX, options, fileName) => {
  const bytes = buildStyledWorkbook(XLSX, options);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
